import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { RootState } from '../index';
import {
  getUsageByUserId,
  saveUserDetails,
  saveUserUsage,
} from './AccessLimitApi';
import {
  setAuthUserToLocalStorage,
  setSlideViewsLimitToLocalStorage,
} from '../../helper';

export interface IUserUsage {
  user: string;
  slideViews: number;
  quizAttempts: number;
  videoWatches: number;
}

export interface ILimitation {
  slideViews?: number;
  quizAttempts?: number;
  videoWatches?: number;
}

export interface IAccessLimitState {
  status: 'idle' | 'loading' | 'rejected';
  leadGenModalOpen: boolean;
  premiumModalOpen: boolean;
  isLeadGenSuccess: boolean;
  limitation: ILimitation | undefined;
  usage: IUserUsage;
  lpAccess: boolean;
}

const initialState: IAccessLimitState = {
  status: 'idle',
  leadGenModalOpen: false,
  premiumModalOpen: false,
  isLeadGenSuccess: false,
  limitation: undefined,
  usage: {
    user: '',
    slideViews: 0,
    quizAttempts: 0,
    videoWatches: 0,
  },
  lpAccess: false,
};

export const AccessLimitSlice = createSlice({
  name: 'access',
  initialState,
  reducers: {
    toggleLeadGenModal: (state) => {
      state.leadGenModalOpen = !state.leadGenModalOpen;
    },
    togglePremiumModal: (state) => {
      state.premiumModalOpen = !state.premiumModalOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save User Usage
      .addCase(saveUserUsage.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveUserUsage.fulfilled, (state, action) => {
        state.status = 'idle';
        state.usage = { ...state.usage, ...action.payload };
      })
      .addCase(saveUserUsage.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get User Usage
      .addCase(getUsageByUserId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUsageByUserId.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.usage) {
          state.usage = { ...state.usage, ...action.payload.usage };
        }
        state.limitation = action.payload.limitation;
        if (action.payload.limitation && action.payload.limitation.slideViews) {
          setSlideViewsLimitToLocalStorage(
            action.payload.limitation.slideViews.toString()
          );
        }
        state.lpAccess = action.payload.lpAccess;
      })
      .addCase(getUsageByUserId.rejected, (state) => {
        state.status = 'rejected';
      })

      // Save User Details
      .addCase(saveUserDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveUserDetails.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.tutopiyaUserId) {
          Cookies.set('tutopiya_user_id', action.payload.tutopiyaUserId);
        } else {
          setAuthUserToLocalStorage(action.payload);
        }
        state.isLeadGenSuccess = true;
      })
      .addCase(saveUserDetails.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const {
  toggleLeadGenModal,
  togglePremiumModal,
} = AccessLimitSlice.actions;

export const getAccessLimitState = (state: RootState): RootState['access'] =>
  state.access;

export default AccessLimitSlice.reducer;
